'use client';

import { FC, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceSubscriptionsOptimized } from '@/hooks/useSubscription';
import { usePricingInfo } from '@/hooks/usePricingQuery';

/**
 * Compact AI + enrichment credit usage bars for the sidebar footer.
 *
 * Shows REMAINING balance against the plan/trial grant — derived from the actual
 * ai_credit_balance / enrichment_credit_balance, NOT notional usage. Onboarding /
 * included usage never decrements the balance, so a fresh trial reads ~full here
 * even though the billing breakdown shows the (free) value delivered. Clicking
 * opens Settings → Billing & Usage.
 */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `${Math.max(0, Math.round(n))}`;
}

interface Bar {
  label: string;
  remaining: number;
  total: number;
  color: string;
}

const SidebarCreditUsage: FC = () => {
  const router = useRouter();
  const workspaceContext = useWorkspace();
  const workspaceId = workspaceContext?.activeWorkspace?.vendorid;

  const { data } = useWorkspaceSubscriptionsOptimized({
    workspaceId,
    enabled: !!workspaceId,
  });
  const { data: pricingData } = usePricingInfo();

  const activeSub = useMemo(() => {
    const subs = (data?.subscriptions ?? []) as any[];
    return (
      subs.find((s) => (s.status === 'active' || s.status === 'trial') && s.product_module === 'sales')
      ?? subs.find((s) => s.status === 'active' || s.status === 'trial')
      ?? null
    );
  }, [data]);

  const bars: Bar[] | null = useMemo(() => {
    if (!activeSub) return null;
    // Enterprise plans have unlimited AI (enterprise_ai_unlimited) and bespoke
    // enrichment, so a "X / Y remaining" bar is meaningless and misleading for
    // them. Prod data confirms enterprise === plan 'enterprise' (and carries
    // is_enterprise / enterprise_ai_unlimited on the workspace). Show the bars
    // only for self-serve trials & paid tiers (starter/standard/pro).
    const isEnterprise = activeSub.plan === 'enterprise'
      || workspaceContext?.isEnterprise === true
      || (workspaceContext?.activeWorkspace as any)?.enterprise_ai_unlimited === true;
    if (isEnterprise) return null;

    const isTrial = activeSub.status === 'trial';
    const plan = (activeSub.plan || 'outreach') as 'prospect' | 'outreach' | 'growth' | 'starter' | 'standard' | 'pro';

    const aiTotal = isTrial
      ? (pricingData?.trial?.ai_credits ?? 1_000_000)
      : (pricingData?.tiers?.[plan]?.ai_credits ?? pricingData?.standard?.ai_credits ?? 18_000_000);
    const enrichmentTotal = isTrial
      ? (pricingData?.trial?.enrichment_credits ?? 300)
      : (pricingData?.tiers?.[plan]?.enrichment_credits ?? pricingData?.standard?.enrichment_credits ?? 5_000);

    const aiBalance = Number(activeSub.ai_credit_balance ?? 0);
    const enrichmentBalance = Number(activeSub.enrichment_credit_balance ?? 0);

    return [
      { label: 'AI credits', remaining: aiBalance, total: aiTotal, color: 'mbn-credit-ai' },
      { label: 'Enrichment', remaining: enrichmentBalance, total: enrichmentTotal, color: 'mbn-credit-enrichment' },
    ];
  }, [activeSub, pricingData, workspaceContext?.isEnterprise, workspaceContext?.activeWorkspace]);

  if (!bars) return null;

  return (
    <button
      type="button"
      onClick={() => router.push('/settings?tab=billing-and-usage')}
      className="mbn-credits"
      title="View billing & usage"
    >
      <div className="mbn-credit-bars">
        {bars.map((bar) => {
          const total = bar.total > 0 ? bar.total : 1;
          const remainingPct = Math.max(0, Math.min(100, Math.round((bar.remaining / total) * 100)));
          const low = remainingPct <= 15;
          return (
            <div key={bar.label}>
              <div className="mbn-credit-label">
                <span className="mbn-credit-caption">{bar.label}</span>
                <span className={`mbn-credit-count ${low ? 'mbn-credit-low' : ''}`}>
                  {formatCompact(bar.remaining)}
                  <span className="mbn-credit-total"> / {formatCompact(bar.total)}</span>
                </span>
              </div>
              <div className="mbn-credit-track">
                <div
                  className={`mbn-credit-fill ${low ? 'mbn-credit-low' : bar.color}`}
                  style={{ width: `${remainingPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </button>
  );
};

export default SidebarCreditUsage;
